import { useState } from "react";
import { ChartBar, CaretRight, CaretDown } from "@phosphor-icons/react";
import { EmptyState } from "../shared/EmptyState";

const KIND_LABEL = { stat: "Statistic", adj: "Adjustment", result: "Result" };
const KIND_COLOR = { stat: "text-sky-400", adj: "text-amber-400", result: "text-emerald-400" };

const ComponentRow = ({ label, kind, value, testId }) => (
  <div data-testid={testId} className="flex items-center justify-between gap-3 border-b border-white/5 py-1.5 last:border-0">
    <span className="font-mono text-[11px] text-zinc-500">
      {label}
      {kind && <span className={`ml-1.5 font-mono text-[9px] uppercase tracking-wide ${KIND_COLOR[kind]}`}>{KIND_LABEL[kind]}</span>}
    </span>
    <span className="font-mono text-xs text-zinc-200">{value}</span>
  </div>
);

const platoonValue = (side) =>
  side?.applied ? `${side.value.toFixed(2)} runs/gm vs ${side.hand}HP` : side?.status === "insufficient_data" ? "Insufficient Data" : "Unavailable";

const pitchingValue = (side) =>
  side?.applied
    ? `${side.starter_era.toFixed(2)} ERA over ${side.starter_innings.toFixed(1)} IP + ${side.bullpen_era.toFixed(2)} bullpen ERA over ${side.bullpen_innings.toFixed(1)} IP`
    : side?.reason || "Unavailable";

const lineupValue = (side) =>
  side?.applied ? `${side.clamped_ratio.toFixed(3)}x lineup-strength scale (raw ratio ${side.ops_ratio.toFixed(3)})` : side?.reason || "Unavailable";

const weatherValue = (weather) => {
  if (!weather?.applied) return weather?.reason || "Unavailable";
  const parts = [`${weather.temp_component >= 0 ? "+" : ""}${weather.temp_component.toFixed(2)} runs (temp)`];
  if (weather.wind_component) parts.push(`${weather.wind_component >= 0 ? "+" : ""}${weather.wind_component.toFixed(2)} runs (${weather.wind_note})`);
  return parts.join(", ");
};

export const RunEnvironmentV2Card = ({ totalV2, awayLabel, homeLabel, testId }) => {
  const [detailsOpen, setDetailsOpen] = useState(false);

  if (!totalV2) {
    return (
      <div data-testid={testId} className="flex flex-col gap-3">
        <EmptyState icon={ChartBar} title="Run Environment V2 Unavailable" description="Loading analytical model data." testId={`${testId}-empty`} />
      </div>
    );
  }

  const ok = totalV2.status === "ok";
  const c = totalV2.components || {};

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">Analytical Run-Environment Model, V2</p>
        <span data-testid={`${testId}-badge`} className="rounded-full border border-eb-accent/30 bg-eb-accent/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-eb-accent">
          Experimental
        </span>
      </div>

      {ok ? (
        <>
          <div className="rounded-lg border border-white/10 bg-eb-bg p-4 text-center">
            <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">V2 Projected Total Runs</span>
            <p data-testid={`${testId}-projected-total`} className="mt-1 font-heading text-3xl text-white">
              {totalV2.projected_total.toFixed(2)}
            </p>
            <p data-testid={`${testId}-breakdown`} className="mt-1 font-mono text-[11px] text-zinc-500">
              {totalV2.expected_home_runs.toFixed(2)} {homeLabel || "Home"} + {totalV2.expected_away_runs.toFixed(2)} {awayLabel || "Away"}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-white/10 bg-eb-bg p-3 text-center">
              <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">{homeLabel || "Home"} Expected Runs</span>
              <p data-testid={`${testId}-home-expected`} className="mt-1 font-heading text-xl text-white">
                {totalV2.expected_home_runs.toFixed(2)}
              </p>
            </div>
            <div className="rounded-lg border border-white/10 bg-eb-bg p-3 text-center">
              <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">{awayLabel || "Away"} Expected Runs</span>
              <p data-testid={`${testId}-away-expected`} className="mt-1 font-heading text-xl text-white">
                {totalV2.expected_away_runs.toFixed(2)}
              </p>
            </div>
          </div>

          <button
            type="button"
            data-testid={`${testId}-details-toggle`}
            onClick={() => setDetailsOpen((o) => !o)}
            className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wide text-zinc-400 transition-colors hover:text-zinc-200"
          >
            {detailsOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
            {detailsOpen ? "Hide projection details" : "View projection details"}
          </button>

          {detailsOpen && (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div data-testid={`${testId}-components-home`} className="rounded-lg border border-white/10 bg-eb-bg p-3">
                  <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">{homeLabel || "Home"} Components</span>
                  <ComponentRow label="Baseline Expected Runs" kind="result" value={c.baseline?.expected_home_runs?.toFixed(2)} testId={`${testId}-home-baseline`} />
                  <ComponentRow
                    label="Opponent-Handedness Run Split"
                    kind="stat"
                    value={platoonValue({ applied: c.platoon?.home_applied, status: c.platoon?.home_status, value: c.platoon?.home_avg_runs_scored, hand: c.platoon?.home_opposing_hand })}
                    testId={`${testId}-home-platoon`}
                  />
                  <ComponentRow label="Starting Pitcher + Bullpen" kind="stat" value={pitchingValue(c.pitching?.home)} testId={`${testId}-home-pitching`} />
                  <ComponentRow label="Confirmed Lineup Strength" kind="stat" value={lineupValue(c.lineup_strength?.home)} testId={`${testId}-home-lineup-strength`} />
                  <ComponentRow label="Final Expected Runs" kind="result" value={totalV2.expected_home_runs.toFixed(2)} testId={`${testId}-home-final`} />
                </div>
                <div data-testid={`${testId}-components-away`} className="rounded-lg border border-white/10 bg-eb-bg p-3">
                  <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">{awayLabel || "Away"} Components</span>
                  <ComponentRow label="Baseline Expected Runs" kind="result" value={c.baseline?.expected_away_runs?.toFixed(2)} testId={`${testId}-away-baseline`} />
                  <ComponentRow
                    label="Opponent-Handedness Run Split"
                    kind="stat"
                    value={platoonValue({ applied: c.platoon?.away_applied, status: c.platoon?.away_status, value: c.platoon?.away_avg_runs_scored, hand: c.platoon?.away_opposing_hand })}
                    testId={`${testId}-away-platoon`}
                  />
                  <ComponentRow label="Starting Pitcher + Bullpen" kind="stat" value={pitchingValue(c.pitching?.away)} testId={`${testId}-away-pitching`} />
                  <ComponentRow label="Confirmed Lineup Strength" kind="stat" value={lineupValue(c.lineup_strength?.away)} testId={`${testId}-away-lineup-strength`} />
                  <ComponentRow label="Final Expected Runs" kind="result" value={totalV2.expected_away_runs.toFixed(2)} testId={`${testId}-away-final`} />
                </div>
              </div>

              <ComponentRow
                label="Park Factor (100 = neutral)"
                value={c.park?.applied ? c.park.run_factor.toFixed(1) : "Not Applied"}
                testId={`${testId}-park-adj`}
              />
              <ComponentRow label="Pregame Weather" kind="adj" value={weatherValue(c.weather)} testId={`${testId}-weather-adj`} />
              {(c.lineup_strength?.home?.missing_regulars?.length > 0 || c.lineup_strength?.away?.missing_regulars?.length > 0) && (
                <p data-testid={`${testId}-missing-regulars`} className="font-mono text-[10px] text-amber-400">
                  Missing regulars tonight: {[...(c.lineup_strength?.home?.missing_regulars || []), ...(c.lineup_strength?.away?.missing_regulars || [])].join(", ")}
                </p>
              )}

              <p data-testid={`${testId}-legend`} className="font-mono text-[10px] text-zinc-500">
                <span className={KIND_COLOR.stat}>Statistic</span> = a real per-game average. <span className={KIND_COLOR.adj}>Adjustment</span> = a
                signed delta contributed to expected runs. <span className={KIND_COLOR.result}>Result</span> = an expected-run value at that step.
              </p>

              <p data-testid={`${testId}-meta`} className="font-mono text-[10px] text-zinc-500">
                Model: {totalV2.model_version} &mdash; a separate, experimental run-environment composition (baseline averages + real
                team-level opponent-handedness run split + integrated starter/bullpen pitching + confirmed-lineup strength + park
                factor + pregame weather). Not a trained/calibrated model, not yet evaluated against the V1 total above, and contains
                no market-comparison or profitability information. Feature version: {totalV2.feature_version}.
              </p>
            </>
          )}
        </>
      ) : (
        <EmptyState icon={ChartBar} title="Insufficient Data For V2 Projection" description={totalV2.reason || "Not enough data to project a V2 total yet."} testId={`${testId}-insufficient`} />
      )}
    </div>
  );
};
